import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { fetchLeadTerritoryOptions } from '@src/api/actions/clientTerritories';

// Lists the territories a client actually has (configured ones, on or off,
// plus "Out Of Area" / legacy labels found on leads). Disabled until a client
// is selected since options are client-scoped.
const TerritoryDropdown = ({ clientId, value, onChange }) => {
  const [territories, setTerritories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!clientId) {
      setTerritories([]);
      setIsError(false);
      return undefined;
    }
    setIsLoading(true);
    setIsError(false);
    fetchLeadTerritoryOptions(clientId)
      .then((response) => {
        if (cancelled) return;
        const list = response?.data?.territories;
        setTerritories(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsError(true);
        setTerritories([]);
        console.error('Error fetching territory options:', error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const selectedOption = useMemo(
    () => territories.find((option) => String(option) === String(value)) || null,
    [territories, value]
  );

  return (
    <Autocomplete
      size="small"
      options={territories}
      value={selectedOption}
      loading={isLoading}
      disabled={!clientId}
      fullWidth
      onChange={(_event, newValue) => onChange(newValue || '')}
      isOptionEqualToValue={(option, val) => String(option) === String(val)}
      getOptionLabel={(option) => option || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Territory"
          placeholder={clientId ? 'All territories' : 'Select a client first'}
          error={isError}
          helperText={isError ? 'Error loading territories.' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default TerritoryDropdown;
